import ComponentToggleSignal from '@/components/component-toggle-signal';
import Modal from '@/components/Modal';
import {useEffect, useMemo, useRef} from 'preact/hooks';

export type GwpGiftModalProps = {
  imageSrc?: string;
  imageAlt?: string;
  title?: string;
  description?: string;
};

/**
 * A modal that displays a gift with purchase.
 * This custom element is intended to nest within the gift thumbnail element,
 * so that clicking the thumbnail opens the modal.
 *
 * @param imageSrc - The image source
 * @param imageAlt - The image alt text
 * @param title - The title
 * @param description - The description
 */
export default function GwpGiftModal({
  imageSrc,
  imageAlt,
  title,
  description,
}: GwpGiftModalProps) {
  const modalController = useMemo(() => new ComponentToggleSignal(), []);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const target = ref.current?.parentElement?.parentElement;
    if (!target) return;

    target.onclick = () => modalController.open();
  }, [ref, modalController]);

  return (
    <div ref={ref} onClick={(e) => e.stopPropagation()}>
      <Modal controller={modalController}>
        <div className="flex h-full flex-col items-center justify-center gap-y-4 text-center">
          <div className="relative h-56">
            <img
              className="size-full object-contain"
              src={imageSrc}
              alt={imageAlt}
            />
          </div>
          <h4>{title}</h4>
          {
            // Description is rich text; it is sanitized in liquid,
            // so we can safely render it as HTML
            description && (
              <p dangerouslySetInnerHTML={{__html: description}}></p>
            )
          }
        </div>
      </Modal>
    </div>
  );
}
